import { bs, type Dictionary, type TranslationKey } from './dictionaries/bs/index'
import { hr } from './dictionaries/hr/index'
import { sr } from './dictionaries/sr/index'
import { en } from './dictionaries/en/index'
import type { Locale } from './locale'

export type { Dictionary, TranslationKey }

/**
 * The four dictionaries, resolved once at module load.
 *
 * `hr` and `sr` are Bosnian with their overrides spread on top — the same
 * arrangement as `App\Support\Lang` on the server, and for the same reason.
 * A flat spread is all that is needed because the dictionaries are flat; there
 * is no nesting for a shallow merge to lose, which is most of why they are flat.
 *
 * Built at load rather than per render: there are four of them, they are small,
 * and doing it eagerly means `translate` is a single object lookup on the hot
 * path instead of a merge behind a memo somebody has to remember to add.
 */
const DICTIONARIES: Record<Locale, Dictionary> = {
  bs,
  hr: { ...bs, ...hr },
  sr: { ...bs, ...sr },
  en,
}

/**
 * Look up a string, substituting `{named}` placeholders.
 *
 * Named rather than positional. `sprintf`-style `%s` reads fine in the language
 * it was written in and becomes a puzzle in translation, because the order of
 * the parts is exactly the thing that moves between languages — and a
 * translator who swaps two `%s` has written a bug they cannot see. `{count}`
 * can be put wherever the sentence needs it.
 *
 * A missing key returns the key. It cannot happen through `t()`, which is typed
 * to the Bosnian file's keys, but this function is also what a runtime value
 * would reach, and a visible `login.submit` on a button is a better failure than
 * an empty one.
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  values?: Record<string, string | number>,
): string {
  const template = DICTIONARIES[locale][key] ?? key

  if (!values) return template

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  )
}

/** Exposed for the tests that check the four dictionaries against each other. */
export const dictionaries = DICTIONARIES
